import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Gift, Loader2, Package, Undo2, X } from "lucide-react";
import {
  adminPackageGrantSchema, formatTry,
  type AccountPackage, type AccountPackagesSummary, type WeddingPackageInfo,
} from "@wedding/shared";
import { adminPlansApi, adminUsersApi } from "../lib/api";
import { cn } from "../lib/utils";

export interface UserWeddingRow {
  id: string;
  slug: string;
  title: string;
  status: string;
  package?: WeddingPackageInfo | null;
}

const GRANT_ERRORS: Record<string, string> = {
  packages_unavailable: "adminPackages.errors.packagesUnavailable",
  wedding_not_found: "adminPackages.errors.weddingNotFound",
  orders_unavailable: "adminPackages.errors.ordersUnavailable",
  // Finansal denetim (2026-10-16)
  payment_ref_required: "adminPackages.errors.paymentRef",
  target_has_package: "adminPackages.errors.targetHasPackage",
  package_not_found: "adminPackages.errors.packageNotFound",
  package_not_sellable: "adminPackages.errors.packageNotSellable",
  reason_required: "adminPackages.errors.reason",
  not_active: "adminPackages.errors.notActive",
  not_found: "adminPackages.errors.notFound",
};
const errText = (t: (k: string, o?: Record<string, unknown>) => string, err: unknown) => {
  const msg = err instanceof Error ? err.message : String(err);
  return GRANT_ERRORS[msg] ? t(GRANT_ERRORS[msg]) : t("adminPackages.errors.generic", { error: msg });
};

/** Düğün paketinin kısa adı — kullanıcı detayındaki düğün tablosu ve "Paket ver" düğün seçimi. */
export function weddingPackageText(t: (k: string, o?: Record<string, unknown>) => string, pkg: WeddingPackageInfo | null | undefined): string {
  if (!pkg) return "—";
  switch (pkg.source) {
    case "order": return `${pkg.label} · ${pkg.orderNumber ?? ""}`.trim();
    case "free": return t("admin.packageFree");
    case "legacy": return `${pkg.label} · ${t("admin.packageLegacy")}`;
    case "suspended": return t("admin.packageSuspended");
    case "account": return `${pkg.label} · ${t("admin.packageAccountWide")}`;
    default: return t("admin.packageTrial");
  }
}

/**
 * Kullanıcı detayı → "Paketler" (paket modeli, 2026-10-16). Hesabın paketleri (kullanılabilir /
 * kullanımda / ödeme bekleyen / biten) + "Paket ver": ödenmiş manuel sipariş (1 sipariş = 1 paket),
 * paket = admin Paketler'deki TANIM (sayıların hepsi tanımdan), tutar 0 ya da indirimli, gerekçe
 * zorunlu; tutar > 0 ise ödeme referansı zorunlu; 10 dk içinde aynı paket → açık onay. Düğün seçilirse
 * ona tanımlanır (Free / geçiş paketi biter). Free / geçiş paketi gerekçeyle "Geri al"ınır (düğünün
 * başka paketi yoksa askıya alınır). Ücretli paket buradan değil, sipariş iptaliyle (iade) geri alınır.
 */
export function UserPackagesCard({
  organizerId, packages, weddings, onChanged, showToast,
}: {
  organizerId: string;
  packages: AccountPackagesSummary;
  weddings: UserWeddingRow[];
  onChanged: () => void;
  showToast: (msg: string, ok?: boolean) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [revoking, setRevoking] = useState<string | null>(null);

  const revoke = async (p: AccountPackage) => {
    const answer = window.prompt(t("adminPackages.revokePrompt", { label: p.label, wedding: p.wedding?.title ?? "—" }));
    if (answer === null) return;
    if (answer.trim().length < 3) {
      showToast(t("adminPackages.errors.reason"), false);
      return;
    }
    setRevoking(p.id);
    try {
      await adminUsersApi.post(`/${organizerId}/packages/${encodeURIComponent(p.id)}/revoke`, { reason: answer.trim() });
      showToast(t("adminPackages.revoked", { label: p.label }));
      onChanged();
    } catch (err) {
      showToast(errText(t, err), false);
    } finally {
      setRevoking(null);
    }
  };

  const groups: Array<{ key: "available" | "inUse" | "pending" | "ended"; items: AccountPackage[] }> = [
    { key: "available", items: packages.available },
    { key: "inUse", items: packages.inUse },
    { key: "pending", items: packages.pending },
    { key: "ended", items: packages.ended },
  ];
  const sourceText = (p: AccountPackage) =>
    p.source === "free" ? t("admin.packageFree") : p.source === "legacy" ? t("admin.packageLegacy") : (p.number ?? "");

  return (
    <div data-testid="user-packages">
      <div className="flex items-center gap-4 px-5 py-4">
        <p className="text-sm text-muted-foreground w-28 shrink-0">{t("adminPackages.title")}</p>
        <p className="flex-1 text-xs text-muted-foreground">{t("adminPackages.modeNote")}</p>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-border text-xs font-semibold text-foreground hover:border-primary/40 hover:text-primary hover:bg-primary/5 transition-all cursor-pointer"
        >
          <Gift className="h-3.5 w-3.5" aria-hidden="true" />
          <span>{t("adminPackages.grant")}</span>
        </button>
      </div>
      <div className="grid gap-3 px-5 pb-5 sm:grid-cols-2">
        {groups.map(({ key, items }) => (
          <div key={key} className="rounded-xl border border-border p-3" data-testid={`user-packages-${key}`}>
            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              {t(key === "ended" ? "packages.endedTitle" : `packages.${key}`)} <span className="tabular-nums">({items.length})</span>
            </p>
            {items.length === 0 ? (
              <p className="mt-1 text-xs text-muted-foreground/60">—</p>
            ) : (
              <ul className="mt-1.5 space-y-1">
                {items.map((p) => (
                  <li key={p.id} className="flex flex-wrap items-center gap-x-2 text-xs">
                    <Package className="h-3 w-3 shrink-0 text-primary" aria-hidden="true" />
                    <span className="font-semibold">{p.label}</span>
                    <span className="text-muted-foreground tabular-nums">{t("adminPackages.guests", { n: p.limits.guests })}</span>
                    <span className="font-mono text-[10px] text-muted-foreground">{sourceText(p)}</span>
                    {p.wedding && <span className="truncate text-muted-foreground">→ {p.wedding.title}</span>}
                    {p.endedReason && <span className="text-muted-foreground">· {t(`packages.ended.${p.endedReason}`)}</span>}
                    {(p.source === "free" || p.source === "legacy") && (p.status === "available" || p.status === "assigned") && (
                      <button
                        type="button"
                        onClick={() => void revoke(p)}
                        disabled={revoking !== null}
                        data-testid={`revoke-${p.id}`}
                        className="ml-auto inline-flex items-center gap-1 rounded-md border border-border px-2 py-0.5 text-[11px] font-medium text-muted-foreground hover:text-red-600 hover:border-red-500/40 disabled:opacity-50 cursor-pointer"
                      >
                        {revoking === p.id ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" /> : <Undo2 className="h-3 w-3" aria-hidden="true" />}
                        <span>{t("adminPackages.revoke")}</span>
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
      {open && (
        <GrantModal
          organizerId={organizerId}
          weddings={weddings.filter((w) => w.status === "active")}
          onClose={() => setOpen(false)}
          onDone={(msg) => {
            setOpen(false);
            showToast(msg);
            onChanged();
          }}
          showToast={showToast}
        />
      )}
    </div>
  );
}

type SellablePackage = { slug: string; label: string; price: number | null; guests: number };
type PlanApiRow = { slug: string; label: string; price_try?: number | null; max_guests_per_wedding: number; is_active: number; is_default: number };

function GrantModal({ organizerId, weddings, onClose, onDone, showToast }: {
  organizerId: string;
  weddings: UserWeddingRow[];
  onClose: () => void;
  onDone: (msg: string) => void;
  showToast: (msg: string, ok?: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const backdropRef = useRef<HTMLDivElement>(null);
  const [packages, setPackages] = useState<SellablePackage[] | null>(null);
  const [f, setF] = useState({ package: "", amount: "0", paymentNote: "", reason: "", weddingSlug: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    // Verilebilir paketler = aktif tanımlar (Free / varsayılan hariç) — sayılar tanımdan.
    adminPlansApi
      .get<PlanApiRow[]>("/")
      .then((r) => {
        if (!alive) return;
        const list = (r.data ?? [])
          .filter((p) => p.is_active && !p.is_default)
          .map((p) => ({ slug: p.slug, label: p.label, price: typeof p.price_try === "number" ? p.price_try : null, guests: p.max_guests_per_wedding }));
        setPackages(list);
        setF((p) => (p.package ? p : { ...p, package: list[0]?.slug ?? "" }));
      })
      .catch(() => alive && setPackages([]));
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => {
      alive = false;
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setF((p) => ({ ...p, [k]: e.target.value }));
  const amount = Number(f.amount.replace(/\./g, "") || "0");
  const body = {
    package: f.package,
    amount,
    reason: f.reason.trim(),
    ...(f.paymentNote.trim() ? { paymentNote: f.paymentNote.trim() } : {}),
    ...(f.weddingSlug ? { weddingSlug: f.weddingSlug } : {}),
  };
  // Tutarlı paket = tahsilat: ödeme referansı (dekont / havale açıklaması) zorunlu.
  const needsRef = amount > 0 && !f.paymentNote.trim();
  const valid = adminPackageGrantSchema.safeParse(body).success && !!f.package && !needsRef;

  const send = async (confirmDuplicate: boolean): Promise<void> => {
    const res = await adminUsersApi.post<{ number: string; wedding: { slug: string; title: string } | null }>(
      `/${organizerId}/packages`,
      confirmDuplicate ? { ...body, confirmDuplicate: true } : body,
    );
    const d = res.data;
    onDone(d?.wedding ? t("adminPackages.grantedTo", { number: d.number, title: d.wedding.title }) : t("adminPackages.granted", { number: d?.number ?? "" }));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid || saving) return;
    setSaving(true);
    try {
      await send(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      // Aynı kullanıcıya aynı paket son 10 dk'da verildiyse: iki sekme / yeniden deneme mi, bilerek mi?
      if (msg === "duplicate_grant" && window.confirm(t("adminPackages.duplicateConfirm"))) {
        try {
          await send(true);
        } catch (err2) {
          showToast(errText(t, err2), false);
        }
      } else if (msg === "duplicate_grant") {
        showToast(t("adminPackages.duplicateSkipped"), false);
      } else {
        showToast(errText(t, err), false);
      }
    } finally {
      setSaving(false);
    }
  };

  const input = "w-full rounded-xl border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20";
  const label = "block space-y-1 text-xs font-medium text-foreground";
  return (
    <div
      ref={backdropRef}
      onClick={(e) => e.target === backdropRef.current && onClose()}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
    >
      <form
        onSubmit={(e) => void submit(e)}
        role="dialog"
        aria-modal="true"
        aria-labelledby="grant-title"
        data-testid="grant-modal"
        className="w-full max-w-lg max-h-[calc(100vh-2rem)] overflow-y-auto bg-card border border-border rounded-2xl shadow-2xl"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <p id="grant-title" className="text-sm font-semibold text-foreground">{t("adminPackages.grantTitle")}</p>
          <button type="button" onClick={onClose} aria-label={t("common.cancel")} className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-3 p-5">
          <p className="text-xs text-muted-foreground leading-relaxed">{t("adminPackages.grantHint")}</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className={cn(label, "sm:col-span-2")}>
              <span>{t("adminPackages.plan")}</span>
              <select value={f.package} onChange={set("package")} disabled={!packages?.length} data-testid="grant-package" className={cn(input, "cursor-pointer")}>
                {packages?.map((p) => (
                  <option key={p.slug} value={p.slug}>
                    {t("siteAdmin.orders.packageOption", {
                      label: p.label,
                      guests: new Intl.NumberFormat(i18n.language).format(p.guests),
                      price: p.price === null ? t("landing.plans.priceQuote") : formatTry(p.price, i18n.language),
                    })}
                  </option>
                ))}
              </select>
              {packages !== null && packages.length === 0 && <span className="block text-[11px] text-red-500">{t("siteAdmin.orders.noPackages")}</span>}
            </label>
            <label className={label}>
              <span>{t("adminPackages.amount")}</span>
              <input inputMode="numeric" value={f.amount} onChange={set("amount")} className={input} />
            </label>
            {amount > 0 && (
              <label className={label}>
                <span>{t("adminPackages.paymentNote")}</span>
                <input
                  value={f.paymentNote}
                  onChange={set("paymentNote")}
                  required
                  maxLength={300}
                  autoComplete="off"
                  aria-invalid={needsRef || undefined}
                  placeholder={t("siteAdmin.orders.paymentNotePh")}
                  data-testid="grant-payment-note"
                  className={input}
                />
              </label>
            )}
            <label className={cn(label, "sm:col-span-2")}>
              <span>{t("adminPackages.wedding")}</span>
              <select value={f.weddingSlug} onChange={set("weddingSlug")} className={cn(input, "cursor-pointer")} data-testid="grant-wedding">
                <option value="">{t("adminPackages.weddingNone")}</option>
                {weddings.map((w) => (
                  <option key={w.slug} value={w.slug}>
                    {t("adminPackages.weddingOption", { title: w.title, pkg: weddingPackageText(t, w.package) })}
                  </option>
                ))}
              </select>
            </label>
            <label className={cn(label, "sm:col-span-2")}>
              <span>{t("adminPackages.reason")}</span>
              <input value={f.reason} onChange={set("reason")} required minLength={3} maxLength={300} placeholder={t("adminPackages.reasonPh")} autoComplete="off" className={input} />
            </label>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 pb-5">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted transition-colors cursor-pointer">
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            disabled={!valid || saving}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
          >
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
            <span>{t("adminPackages.submit")}</span>
          </button>
        </div>
      </form>
    </div>
  );
}
